import React from 'react';
import { View } from 'react-native';

import Text from '../../../components/text/Text';
import { StatusBadge, Tag } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { isRowAssignedToPicker } from '../../myDay/usePickerMyDay';
import PickingWorkRow from './PickingWorkRow';

/**
 * One route on Picking My Day: route name, planned time, driver, who packs it
 * and its machines in route order. Completed groups show their picks with the
 * tub sticker action.
 * Props: group (usePickerMyDay route group), userId, isCompleted,
 * printingLabelDocketId, onOpenRow(row)
 */
const PickingRouteGroup = ({ group, userId, isCompleted = false, printingLabelDocketId, onOpenRow }) => {
	const styles = useThemedStyles(createStyles);
	const ownedRowCount = group.rows.filter(row => isRowAssignedToPicker(row, userId)).length;
	const pickerNames = group.assignedPickers.length
		? group.assignedPickers
			.map(picker => (picker.id === userId ? `${picker.name} (you)` : picker.name))
			.join(', ')
		: 'No pickers assigned';

	return (
		<View style={styles.group}>
			<View style={styles.header}>
				<View style={styles.titles}>
					<Text style={styles.name} accessibilityRole="header">{group.routeName}</Text>
					<Text style={styles.planned}>{group.plannedLabel}</Text>
				</View>
				{isCompleted ? (
					<StatusBadge label="Completed" tone="success" size="small" />
				) : (
					<Tag label={`${ownedRowCount} of ${group.rows.length} yours`} tone={ownedRowCount > 0 ? 'primary' : 'neutral'} />
				)}
			</View>
			<View style={styles.meta}>
				<View style={styles.metaLine}>
					<Text style={styles.metaLabel}>Driver</Text>
					<Text style={styles.metaValue}>{group.driverName}</Text>
				</View>
				{!isCompleted && (
					<View style={styles.metaLine}>
						<Text style={styles.metaLabel}>Packing</Text>
						<Text style={styles.metaValue}>{pickerNames}</Text>
					</View>
				)}
			</View>
			{group.rows.map((row, rowIndex) => (
				<PickingWorkRow
					key={row.id}
					row={row}
					routeOrder={rowIndex + 1}
					isOwned={isRowAssignedToPicker(row, userId)}
					isCompleted={isCompleted}
					isPrintingLabel={printingLabelDocketId === row.id}
					onOpen={onOpenRow}
				/>
			))}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	group: {
		gap: space.sm,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.sm,
	},
	titles: {
		flex: 1,
		gap: 2,
	},
	name: {
		...type.sectionTitle,
		color: colors.text,
	},
	planned: {
		...type.secondary,
		color: colors.textSecondary,
	},
	meta: {
		gap: 2,
		marginBottom: space.xs,
	},
	metaLine: {
		flexDirection: 'row',
		gap: space.sm,
	},
	metaLabel: {
		...type.secondarySemibold,
		color: colors.textSecondary,
		width: 64,
	},
	metaValue: {
		...type.secondary,
		color: colors.text,
		flex: 1,
	},
});

export default React.memo(PickingRouteGroup);
